import type { RowDropPosition } from "@design-system/ViewTable/ViewTable.lib";
import type {
    NestedShowcaseRow,
    ReorderRow,
} from "./viewTablePlaygroundShared";

type FlatInsertPosition = "before" | "after";

const resolveFlatInsertPosition = (
    dropPosition: RowDropPosition,
): FlatInsertPosition => (dropPosition === "before" ? "before" : "after");

export const moveFlatRow = ({
    rows,
    activeId,
    overId,
    dropPosition,
}: {
    rows: ReorderRow[];
    activeId: ReorderRow["id"];
    overId: ReorderRow["id"];
    dropPosition: RowDropPosition;
}): ReorderRow[] => {
    if (activeId === overId) {
        return rows;
    }
    const activeRow = rows.find((row) => row.id === activeId);
    if (!activeRow) {
        return rows;
    }
    const remainingRows = rows.filter((row) => row.id !== activeId);
    const overIndex = remainingRows.findIndex((row) => row.id === overId);
    if (overIndex < 0) {
        return rows;
    }
    const offset = resolveFlatInsertPosition(dropPosition) === "after" ? 1 : 0;
    return [
        ...remainingRows.slice(0, overIndex + offset),
        activeRow,
        ...remainingRows.slice(overIndex + offset),
    ];
};

type NestedDropValidation =
    | {
          isValid: false;
          destinationParentId: null;
          referenceId: null;
          insertPosition: null;
      }
    | {
          isValid: true;
          destinationParentId: NestedShowcaseRow["id"] | null;
          referenceId: NestedShowcaseRow["id"] | null;
          insertPosition: "before" | "after";
      };

interface NestedDropArgs {
    rows: NestedShowcaseRow[];
    activeId: NestedShowcaseRow["id"];
    overId: NestedShowcaseRow["id"];
    dropPosition: RowDropPosition;
}

interface LocatedNestedRow {
    row: NestedShowcaseRow;
    parentId: NestedShowcaseRow["id"] | null;
}

const findNestedRow = ({
    rows,
    id,
    parentId = null,
}: {
    rows: NestedShowcaseRow[];
    id: NestedShowcaseRow["id"];
    parentId?: NestedShowcaseRow["id"] | null;
}): LocatedNestedRow | null => {
    for (const row of rows) {
        if (row.id === id) {
            return { row, parentId };
        }

        const locatedRow = findNestedRow({
            rows: row.children,
            id,
            parentId: row.id,
        });
        if (locatedRow) {
            return locatedRow;
        }
    }

    return null;
};

const hasNestedRow = (
    rows: NestedShowcaseRow[],
    id: NestedShowcaseRow["id"],
): boolean =>
    rows.some((row) => row.id === id || hasNestedRow(row.children, id));

const rejectNestedDrop = (): NestedDropValidation => ({
    isValid: false,
    destinationParentId: null,
    referenceId: null,
    insertPosition: null,
});

export const validateNestedDrop = ({
    rows,
    activeId,
    overId,
    dropPosition,
}: NestedDropArgs): NestedDropValidation => {
    if (activeId === overId) {
        return rejectNestedDrop();
    }

    const activeRow = findNestedRow({ rows, id: activeId });
    if (!activeRow) {
        return rejectNestedDrop();
    }

    const targetRow = findNestedRow({ rows, id: overId });
    if (!targetRow) {
        return rejectNestedDrop();
    }

    if (hasNestedRow(activeRow.row.children, overId)) {
        return rejectNestedDrop();
    }

    if (dropPosition === "on" && targetRow.row.kind === "group") {
        return {
            isValid: true,
            destinationParentId: overId,
            referenceId: null,
            insertPosition: "after",
        };
    }

    return {
        isValid: true,
        destinationParentId: targetRow.parentId,
        referenceId: overId,
        insertPosition: dropPosition === "before" ? "before" : "after",
    };
};

interface NestedRemoval {
    rows: NestedShowcaseRow[];
    removedRow: NestedShowcaseRow | null;
}

const removeNestedRow = ({
    rows,
    id,
}: {
    rows: NestedShowcaseRow[];
    id: NestedShowcaseRow["id"];
}): NestedRemoval => {
    const rowIndex = rows.findIndex((row) => row.id === id);
    if (rowIndex >= 0) {
        return {
            rows: [...rows.slice(0, rowIndex), ...rows.slice(rowIndex + 1)],
            removedRow: rows[rowIndex],
        };
    }

    for (const [index, row] of rows.entries()) {
        const removal = removeNestedRow({ rows: row.children, id });
        if (removal.removedRow) {
            return {
                rows: [
                    ...rows.slice(0, index),
                    { ...row, children: removal.rows },
                    ...rows.slice(index + 1),
                ],
                removedRow: removal.removedRow,
            };
        }
    }

    return { rows, removedRow: null };
};

const insertIntoSiblings = ({
    rows,
    row,
    referenceId,
    insertPosition,
}: {
    rows: NestedShowcaseRow[];
    row: NestedShowcaseRow;
    referenceId: NestedShowcaseRow["id"] | null;
    insertPosition: "before" | "after";
}): NestedShowcaseRow[] | null => {
    if (referenceId === null) {
        return [...rows, row];
    }

    const referenceIndex = rows.findIndex(
        (candidate) => candidate.id === referenceId,
    );
    if (referenceIndex < 0) {
        return null;
    }

    const offset = insertPosition === "after" ? 1 : 0;
    const insertIndex = referenceIndex + offset;
    return [...rows.slice(0, insertIndex), row, ...rows.slice(insertIndex)];
};

const insertNestedRow = ({
    rows,
    row,
    destinationParentId,
    referenceId,
    insertPosition,
}: {
    rows: NestedShowcaseRow[];
    row: NestedShowcaseRow;
    destinationParentId: NestedShowcaseRow["id"] | null;
    referenceId: NestedShowcaseRow["id"] | null;
    insertPosition: "before" | "after";
}): NestedShowcaseRow[] | null => {
    if (destinationParentId === null) {
        return insertIntoSiblings({
            rows,
            row,
            referenceId,
            insertPosition,
        });
    }

    for (const [index, candidate] of rows.entries()) {
        if (candidate.id === destinationParentId) {
            const children = insertIntoSiblings({
                rows: candidate.children,
                row,
                referenceId,
                insertPosition,
            });
            if (!children) {
                return null;
            }

            return [
                ...rows.slice(0, index),
                { ...candidate, children },
                ...rows.slice(index + 1),
            ];
        }

        const children = insertNestedRow({
            rows: candidate.children,
            row,
            destinationParentId,
            referenceId,
            insertPosition,
        });
        if (children) {
            return [
                ...rows.slice(0, index),
                { ...candidate, children },
                ...rows.slice(index + 1),
            ];
        }
    }

    return null;
};

interface NestedMoveResult {
    rows: NestedShowcaseRow[];
    destinationGroupId: NestedShowcaseRow["id"] | null;
}

export const moveNestedRow = (args: NestedDropArgs): NestedMoveResult => {
    const validation = validateNestedDrop(args);
    if (!validation.isValid) {
        return { rows: args.rows, destinationGroupId: null };
    }

    const removal = removeNestedRow({
        rows: args.rows,
        id: args.activeId,
    });
    if (!removal.removedRow) {
        return { rows: args.rows, destinationGroupId: null };
    }

    const rows = insertNestedRow({
        rows: removal.rows,
        row: removal.removedRow,
        destinationParentId: validation.destinationParentId,
        referenceId: validation.referenceId,
        insertPosition: validation.insertPosition,
    });
    if (!rows) {
        return { rows: args.rows, destinationGroupId: null };
    }

    return {
        rows,
        destinationGroupId:
            validation.referenceId === null
                ? validation.destinationParentId
                : null,
    };
};

interface FlattenedNestedRow {
    id: NestedShowcaseRow["id"];
    name: string;
    depth: number;
}

const flattenNestedRowsAtDepth = (
    rows: NestedShowcaseRow[],
    depth: number,
): FlattenedNestedRow[] =>
    rows.flatMap((row) => [
        { id: row.id, name: row.name, depth },
        ...flattenNestedRowsAtDepth(row.children, depth + 1),
    ]);

export const flattenNestedRows = (
    rows: NestedShowcaseRow[],
): FlattenedNestedRow[] => flattenNestedRowsAtDepth(rows, 0);
